import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { LandingPhoto } from './BookingPage';
import { Photo, photoUrl } from './LandingTemplates';

/*
 * A gallery for a one-off page: a strip of thumbnails laid in a brick pattern (two lanes, the
 * second offset by half a tile) that crawls on its own, beside one large selected photo.
 * Clicking a thumbnail makes it the large one. Built for Show Shears; nothing in it is hers
 * beyond the colors passed in.
 *
 * Desktop: the strip is a vertical column, a third of the width, as tall as the large photo.
 * Phone: the large photo comes first and the strip runs sideways under it, because a third of
 * a phone is a column of thumbnails nobody could tap.
 *
 * ⚠️ THE CRAWL IS A TRANSFORM, NOT scrollTop. At 14px/s a frame moves a quarter of a pixel,
 * and scrollTop rounds to whole pixels on a 1x screen, so a scroll-driven crawl simply never
 * moves there. The strip is therefore not a native scroller: wheel and drag are handled here,
 * and the loop is three-plus stacked copies of the photos with the offset wrapped inside one
 * copy's length, so there is never an end to hit.
 *
 * ⚠️ AN ODD NUMBER OF PHOTOS IS DOUBLED. Two lanes only tile seamlessly when both hold the
 * same count; with five photos the lanes would be three and two long and the loop would show
 * a hole at the seam. Ten (five twice) tiles cleanly.
 */

const GAP = 12;
const SPEED = 14; // px per second
const RESUME_AFTER = 2500; // ms of stillness after a wheel or drag before the crawl resumes
const TILE_ASPECT = 4 / 3; // height ÷ width — a phone photo held upright, so most thumbnails lose nothing
const PHONE_LANE = 120; // thumbnail height in the sideways strip

function useWide() {
  const query = '(min-width: 768px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}

export function BrickGallery({ photos, accent, border }: { photos: LandingPhoto[]; accent: string; border: string }) {
  const [sel, setSel] = useState(0);
  const vertical = useWide();
  const viewport = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const big = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  const offset = useRef(0);
  const pausedUntil = useRef(0);
  const holding = useRef(false); // hovered, or a thumbnail has focus
  const drag = useRef<{ from: number; at: number; moved: boolean } | null>(null);
  const swallowClick = useRef(false);

  useLayoutEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [vertical]);

  const loop = photos.length % 2 ? [...photos, ...photos] : photos;
  const rows = loop.length / 2;
  // Tile size across the strip (cross) and along it (main), and one row's pitch along it.
  const cross = vertical ? Math.max(0, (box.w - GAP) / 2) : PHONE_LANE;
  const main = vertical ? cross * TILE_ASPECT : PHONE_LANE / TILE_ASPECT;
  const step = main + GAP;
  const period = rows * step;
  const span = vertical ? box.h : box.w;
  const copies = period > 0 ? Math.max(3, Math.ceil(span / period) + 2) : 0;

  const apply = () => {
    if (!track.current || !period) return;
    offset.current = ((offset.current % period) + period) % period;
    const d = -(offset.current + period);
    track.current.style.transform = vertical ? `translate3d(0,${d}px,0)` : `translate3d(${d}px,0,0)`;
  };

  useEffect(() => {
    if (!period) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let last = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const dt = Math.min(0.1, (t - last) / 1000);
      last = t;
      if (!still && !holding.current && !drag.current && t > pausedUntil.current) offset.current += SPEED * dt;
      apply();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // apply reads the latest period/vertical through this effect's own re-run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, vertical]);

  // Wheel needs a non-passive listener to stop the page scrolling under it.
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Sideways strip: only sideways wheel/trackpad moves it, so the page still scrolls.
      const d = vertical ? e.deltaY : Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : 0;
      if (!d) return;
      e.preventDefault();
      offset.current += d;
      pausedUntil.current = performance.now() + RESUME_AFTER;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [vertical]);

  const along = (e: React.PointerEvent) => (vertical ? e.clientY : e.clientX);
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    drag.current = { from: along(e), at: offset.current, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const g = drag.current;
    if (!g) return;
    const d = along(e) - g.from;
    if (!g.moved && Math.abs(d) < 6) return;
    if (!g.moved) {
      g.moved = true;
      viewport.current?.setPointerCapture(e.pointerId);
    }
    offset.current = g.at - d;
  };
  const endDrag = () => {
    if (drag.current?.moved) {
      swallowClick.current = true;
      pausedUntil.current = performance.now() + RESUME_AFTER;
    }
    drag.current = null;
  };

  const wrap = (i: number) => ((i % photos.length) + photos.length) % photos.length;
  const show = (i: number) => setSel(wrap(i));
  const stepBy = (by: number) => setSel((s) => wrap(s + by)); // functional: fast double-clicks both count
  useEffect(() => {
    big.current?.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 350, easing: 'ease-out' });
  }, [sel]);

  // A focused thumbnail is brought into view, so keyboard users are not tabbing off-screen.
  const reveal = (e: React.FocusEvent, pos: number) => {
    holding.current = true;
    if (!e.currentTarget.matches(':focus-visible')) return; // a mouse click focuses too; don't jump
    offset.current = pos - period - (span - main) / 2;
    apply();
  };

  const tiles = [];
  for (let c = 0; c < copies; c++) {
    for (let j = 0; j < loop.length; j++) {
      const i = j % photos.length;
      const lane = j % 2;
      const pos = (c * rows + Math.floor(j / 2)) * step + (lane ? step / 2 : 0);
      const live = c === 1 && j < photos.length; // one tabbable copy of each photo
      tiles.push(
        <button
          key={`${c}-${j}`}
          type="button"
          tabIndex={live ? 0 : -1}
          aria-hidden={live ? undefined : true}
          aria-label={`Show photo ${i + 1} of ${photos.length}`}
          aria-pressed={live ? i === sel : undefined}
          onClick={() => show(i)}
          onFocus={live ? (e) => reveal(e, pos) : undefined}
          onBlur={live ? () => (holding.current = false) : undefined}
          className="absolute overflow-hidden transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{
            [vertical ? 'top' : 'left']: pos,
            [vertical ? 'left' : 'top']: lane * (cross + GAP),
            [vertical ? 'height' : 'width']: main,
            [vertical ? 'width' : 'height']: cross,
            outlineColor: accent,
            boxShadow: i === sel ? `0 0 0 2px ${accent}` : `0 0 0 1px ${border}`,
            opacity: i === sel ? 1 : 0.78,
          }}
        >
          <Photo photo={photos[i]!} className="pointer-events-none h-full w-full object-cover" />
        </button>,
      );
    }
  }

  const current = photos[sel]!;
  const fade = vertical
    ? 'linear-gradient(to bottom, transparent, #000 9%, #000 91%, transparent)'
    : 'linear-gradient(to right, transparent, #000 7%, #000 93%, transparent)';

  return (
    <div className={vertical ? 'grid grid-cols-3 gap-6' : 'flex flex-col gap-5'}>
      <div className={vertical ? 'relative' : 'relative order-2'} style={vertical ? undefined : { height: PHONE_LANE * 2 + GAP }}>
        <div
          ref={viewport}
          className="absolute inset-0 select-none overflow-hidden"
          style={{ touchAction: vertical ? 'none' : 'pan-y', maskImage: fade, WebkitMaskImage: fade, cursor: 'grab' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerEnter={(e) => e.pointerType === 'mouse' && (holding.current = true)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && (holding.current = false)}
          onClickCapture={(e) => {
            if (swallowClick.current) {
              swallowClick.current = false;
              e.stopPropagation();
              e.preventDefault();
            }
          }}
          onDragStart={(e) => e.preventDefault()}
        >
          <div ref={track} className="absolute left-0 top-0 will-change-transform">
            {tiles}
          </div>
        </div>
      </div>

      <figure className={vertical ? 'col-span-2' : 'order-1'}>
        {/*
          The large photo is never cropped and never letterboxed: the stage has a fixed height
          (so nothing below it jumps), and the photo inside keeps its own shape — as tall as
          the stage allows, as wide as that makes it, never wider than the column. Her photos
          come upright, square and one wide, and any single fixed frame either cut heads and
          paws off some of them or padded them with bars. The marble shows around a narrow one.
        */}
        <div className="flex h-[min(70vh,500px)] items-center justify-center md:h-[min(78vh,740px)]">
          <div ref={big} className="relative max-w-full" style={{ boxShadow: `0 0 0 1px ${border}` }}>
            <img
              key={current.id}
              src={photoUrl(current)}
              alt={current.caption ?? ''}
              width={current.width}
              height={current.height}
              className="block h-auto max-h-[min(70vh,500px)] w-auto max-w-full md:max-h-[min(78vh,740px)]"
            />
          </div>
        </div>
        {/* Arrows sit side by side under the photo, the count between them — not over the
            picture, where they covered paws on the upright shots. */}
        <figcaption className="mt-4 flex flex-col items-center gap-3 text-sm">
          {photos.length > 1 && (
            <div className="flex items-center gap-4">
              <Arrow label="Previous photo" onClick={() => stepBy(-1)} accent={accent} border={border}>
                <ChevronLeft className="h-5 w-5" />
              </Arrow>
              <span
                className="min-w-[4.5rem] text-center text-[0.68rem] font-semibold uppercase tracking-[0.22em] tabular-nums"
                style={{ color: accent }}
              >
                {sel + 1} / {photos.length}
              </span>
              <Arrow label="Next photo" onClick={() => stepBy(1)} accent={accent} border={border}>
                <ChevronRight className="h-5 w-5" />
              </Arrow>
            </div>
          )}
          {current.caption && <span className="opacity-80">{current.caption}</span>}
        </figcaption>
      </figure>
    </div>
  );
}

function Arrow({ label, onClick, accent, border, children }: {
  label: string;
  onClick: () => void;
  accent: string;
  border: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid h-10 w-10 place-items-center rounded-full transition-colors hover:bg-white/5"
      style={{ color: accent, boxShadow: `0 0 0 1px ${border}` }}
    >
      {children}
    </button>
  );
}
